import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelLeftOpen } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import type {
  DesignatedReviewPlacementStatus,
  LearnerLibraryResponse,
  LessonProcessDetailView,
  LessonSelectionMeta,
  TestSelection,
  WorkbookMode,
} from '@/types/coaching'
import { pickDefaultLessonId } from '@/hooks/coaching/useFocusLesson'
import { usePersistedCoachingPanelCollapse } from '@/hooks/coaching/usePersistedCoachingPanelCollapse'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import { useDesignatedReviews } from '@/hooks/coaching/useDesignatedReview'
import { useTests } from '@/hooks/coaching/useTests'
import { useUnitAssessments } from '@/hooks/coaching/useUnitAssessments'
import { ContentTreeSection } from './ContentTreeSection'
import { findActiveCumulativeTest, findPendingOfflineTest } from './coachingDefaultTarget'
import { getCurrentCurriculumLessonIds } from './currentCurriculumLessons'
import { LessonGradingPane } from './LessonGradingPane'
import { TestCoachingDetail } from './TestCoachingDetail'
import { CumulativeAssessmentCoachingDetail } from './CumulativeAssessmentCoachingDetail'
import { LessonHistoryModal } from './LessonHistoryModal'
import { LessonProgressDetailPane } from './LessonProgressDetailPane'
import { UnitAssessmentCoachingDetail } from './UnitAssessmentCoachingDetail'
import { DesignatedReviewCoachingDetail } from './DesignatedReviewCoachingDetail'
import { LessonRefreshButton } from './LessonRefreshButton'
import { pickDefaultLibraryLessonId, pickDefaultUnitAssessmentId } from './unitAssessmentSelection'

interface Props {
  learnerId: number
  timeslotId?: number | null
  todayLessons: LessonProcessDetailView[]
  renderLessonActions?: (lesson: LessonProcessDetailView) => React.ReactNode
  workbookModeSelection?: { learnerId: number; lessonId: number; mode: WorkbookMode } | null
  onWorkbookModeChange?: (lessonId: number, mode: WorkbookMode) => void
  onHandheldSelectionChange?: (selection: { learnerId: number; lessonId: number | null; attemptId: number | null; unitAssessmentId: number | null; test: TestSelection | null }) => void
}

const REVIEW_FIRST_STATUSES = new Set<DesignatedReviewPlacementStatus>([
  'LEGACY_IMMEDIATE',
  'DUE',
  'OVERDUE',
  'STARTED',
])

/**
 * 통합 작업영역 — 좌 콘텐츠 트리(항상 표시) + 우 선택 노드 상세.
 * §6-① 정책 A: 트리 레슨 클릭 → 오늘 레슨이면 채점 뷰, 과거 완료면 학습기록 모달, 미시작은 비클릭(트리에서 차단).
 * 테스트 노드 클릭 → 우측 TestCoachingDetail.
 */
export function CoachingWorkArea({
  learnerId,
  timeslotId,
  todayLessons,
  renderLessonActions,
  workbookModeSelection,
  onWorkbookModeChange,
  onHandheldSelectionChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const lessonListPanel = usePersistedCoachingPanelCollapse('lessonListCollapsed')
  const library = useLibrary(learnerId)
  const tests = useTests(learnerId)
  const unitAssessments = useUnitAssessments(learnerId)
  const designatedReviews = useDesignatedReviews(learnerId)
  const todayIds = useMemo(() => todayLessons.map((l) => l.lessonId), [todayLessons])
  const currentCurriculumLessonIds = useMemo(
    () => getCurrentCurriculumLessonIds(library.data),
    [library.data],
  )
  const activeTodayLessons = useMemo(() => {
    return todayLessons.filter((lesson) => {
      if (!currentCurriculumLessonIds.has(lesson.lessonId)) return false
      // Review finalization is independent of the canonical progress components.
      if (lesson.lessonKind === 'designated_review' && lesson.reviewAttemptCompleted != null) {
        return !lesson.reviewAttemptCompleted
      }
      return lesson.status !== 'completed' ||
        (lesson.lessonKind === 'designated_review' &&
          (lesson.reviewComponents?.some((component) => component.status !== 'completed') ||
            lesson.components.some((component) => component.status !== 'completed')))
    })
  }, [todayLessons, currentCurriculumLessonIds])
  const nextLessonId = useMemo(
    () => pickDefaultLessonId(activeTodayLessons) ?? pickDefaultLibraryLessonId(library.data),
    [activeTodayLessons, library.data],
  )
  const pendingOfflineTest = useMemo(
    () => findPendingOfflineTest(library.data?.tree ?? [], tests.data?.tests),
    [library.data?.tree, tests.data?.tests],
  )
  // 학습자 오늘 수업 우선순위와 같다: 오프라인 챕터 테스트 > 누적 테스트 > 다음 레슨.
  const activeCumulativeTest = useMemo(
    () => (pendingOfflineTest == null ? findActiveCumulativeTest(library.data?.tree ?? []) : null),
    [library.data?.tree, pendingOfflineTest],
  )
  const defaultLessonId = pendingOfflineTest?.prerequisiteLessonId ?? nextLessonId
  const defaultUnitAssessmentId = useMemo(() => {
    return pickDefaultUnitAssessmentId(unitAssessments.data ?? [], defaultLessonId)
  }, [defaultLessonId, unitAssessments.data])
  const defaultReviewLessonId = useMemo(() => {
    const activeReviewLessonId = activeTodayLessons.find(
      (lesson) => lesson.lessonKind === 'designated_review',
    )?.lessonId
    if (activeReviewLessonId != null) return activeReviewLessonId

    return designatedReviews.data?.find(
      (review) => currentCurriculumLessonIds.has(review.reviewLessonId) &&
        REVIEW_FIRST_STATUSES.has(review.placementStatus),
    )?.reviewLessonId ?? null
  }, [activeTodayLessons, currentCurriculumLessonIds, designatedReviews.data])
  const defaultTestSelection = defaultUnitAssessmentId == null && defaultReviewLessonId == null
    ? (pendingOfflineTest?.hasExistingTest ? pendingOfflineTest.selection : activeCumulativeTest?.selection ?? null)
    : null

  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(
    defaultUnitAssessmentId == null && defaultReviewLessonId == null && defaultTestSelection == null
      ? defaultLessonId
      : null,
  )
  const [selectedReviewLessonId, setSelectedReviewLessonId] = useState<number | null>(
    defaultUnitAssessmentId == null ? defaultReviewLessonId : null,
  )
  const [selectedLessonMeta, setSelectedLessonMeta] = useState<LessonSelectionMeta | null>(null)
  const [selectedHistoricalLesson, setSelectedHistoricalLesson] = useState<{ id: number; meta: LessonSelectionMeta } | null>(null)
  const [selectedTest, setSelectedTest] = useState<TestSelection | null>(defaultTestSelection)
  const [selectedUnitAssessmentId, setSelectedUnitAssessmentId] = useState<number | null>(defaultUnitAssessmentId)
  const [historyLesson, setHistoryLesson] = useState<{ id: number; title: string } | null>(null)

  useEffect(() => {
    setSelectedLessonId(
      defaultUnitAssessmentId == null && defaultReviewLessonId == null && defaultTestSelection == null
        ? defaultLessonId
        : null,
    )
    setSelectedReviewLessonId(defaultUnitAssessmentId == null ? defaultReviewLessonId : null)
    setSelectedLessonMeta(null)
    setSelectedHistoricalLesson(null)
    setSelectedTest(defaultTestSelection)
    setSelectedUnitAssessmentId(defaultUnitAssessmentId)
  }, [learnerId, defaultLessonId, defaultReviewLessonId, defaultTestSelection, defaultUnitAssessmentId])

  const focusLesson = useMemo(
    () => todayLessons.find((l) => l.lessonId === selectedLessonId) ?? null,
    [todayLessons, selectedLessonId],
  )
  const defaultSelectedLessonMeta = useMemo(
    () => findLessonSelectionMeta(library.data, selectedLessonId ?? selectedReviewLessonId),
    [library.data, selectedLessonId, selectedReviewLessonId],
  )
  const effectiveSelectedLessonMeta = selectedLessonMeta ?? defaultSelectedLessonMeta
  const defaultHistoricalLesson = !focusLesson && selectedLessonId != null && effectiveSelectedLessonMeta?.status === 'completed'
    ? { id: selectedLessonId, meta: effectiveSelectedLessonMeta }
    : null
  const defaultPrestartLesson = !focusLesson && selectedLessonId != null && effectiveSelectedLessonMeta?.status === 'not_started'
    ? { id: selectedLessonId, meta: effectiveSelectedLessonMeta }
    : null
  const selectedReview = designatedReviews.data?.find(
    (review) => review.reviewLessonId === selectedReviewLessonId,
  ) ?? null
  const selectedActiveReview = todayLessons.find(
    (lesson) => lesson.lessonId === selectedReviewLessonId && lesson.lessonKind === 'designated_review',
  )
  const activeReviewLesson = selectedActiveReview
    ? normalizeActiveReviewLesson(selectedActiveReview)
    : null
  const handheldLessonId = activeReviewLesson?.lessonId ?? focusLesson?.lessonId ?? null
  const handheldAttemptId = activeReviewLesson?.reviewAttemptId ?? null
  useEffect(() => {
    onHandheldSelectionChange?.({ learnerId, lessonId: handheldLessonId, attemptId: handheldAttemptId, unitAssessmentId: selectedUnitAssessmentId, test: selectedTest })
  }, [learnerId, handheldLessonId, handheldAttemptId, selectedUnitAssessmentId, selectedTest, onHandheldSelectionChange])

  const workbookModeFor = (lessonId: number): WorkbookMode =>
    workbookModeSelection?.learnerId === learnerId && workbookModeSelection.lessonId === lessonId
      ? workbookModeSelection.mode
      : 'IMMEDIATE'
  function handleSelectLesson(lessonId: number, meta: LessonSelectionMeta) {
    setSelectedTest(null)
    setSelectedUnitAssessmentId(null)
    setSelectedReviewLessonId(null)
    if (todayIds.includes(lessonId)) {
      setSelectedHistoricalLesson(null)
      setSelectedLessonMeta(meta)
      setSelectedLessonId(lessonId) // 오늘 레슨 → 채점
    } else if (meta.status === 'not_started') {
      setSelectedHistoricalLesson(null)
      setSelectedLessonMeta(meta)
      setSelectedLessonId(lessonId)
    } else if (meta.status === 'completed') {
      setSelectedLessonMeta(meta)
      setSelectedLessonId(lessonId)
      setSelectedHistoricalLesson({ id: lessonId, meta })
    } else {
      // Current/in-progress lessons are served by the live response. Never route them
      // to the completed-only detail API if that response is momentarily unavailable.
      setSelectedHistoricalLesson(null)
      setSelectedLessonMeta(meta)
      setSelectedLessonId(lessonId)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className={lessonListPanel.collapsed
        ? 'grid grid-cols-1 gap-0'
        : 'grid grid-cols-1 gap-0 lg:grid-cols-[340px_1fr]'} data-testid="coaching-work-grid">
        <div
          hidden={lessonListPanel.collapsed}
          className="border-b border-gray-100 lg:border-b-0 lg:border-r"
          data-testid="coaching-content-tree-column"
        >
          <ContentTreeSection
            learnerId={learnerId}
            collapsed={false}
            onToggleCollapsed={lessonListPanel.toggle}
            onSelectLesson={handleSelectLesson}
            selectedLessonId={selectedLessonId}
            selectedTest={selectedTest}
            selectedDesignatedReviewLessonId={selectedReviewLessonId}
            onSelectDesignatedReview={(lessonId) => {
              setSelectedReviewLessonId(lessonId)
              setSelectedLessonId(null)
              setSelectedLessonMeta(null)
              setSelectedHistoricalLesson(null)
              setSelectedTest(null)
              setSelectedUnitAssessmentId(null)
            }}
            onSelectTest={(sel) => {
              setSelectedUnitAssessmentId(null)
              setSelectedReviewLessonId(null)
              setSelectedLessonId(null)
              setSelectedLessonMeta(null)
              setSelectedHistoricalLesson(null)
              setSelectedTest(sel)
            }}
            selectedUnitAssessmentId={selectedUnitAssessmentId}
            onSelectUnitAssessment={(id) => {
              setSelectedTest(null)
              setSelectedLessonId(null)
              setSelectedLessonMeta(null)
              setSelectedHistoricalLesson(null)
              setSelectedReviewLessonId(null)
              setSelectedUnitAssessmentId(id)
            }}
          />
        </div>

        <div className="min-w-0 p-3" data-testid="coaching-detail-pane">
          {lessonListPanel.collapsed ? (
            <div className="mb-3 flex items-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-controls="coaching-content-tree-body"
                aria-expanded={false}
                onClick={lessonListPanel.toggle}
              >
                <PanelLeftOpen />
                {t('panelCollapse.lessonList.expand')}
              </Button>
            </div>
          ) : null}
          {selectedUnitAssessmentId != null ? (
            <UnitAssessmentCoachingDetail
              key={`${learnerId}:${selectedUnitAssessmentId}`}
              learnerId={learnerId}
              unitAssessmentId={selectedUnitAssessmentId}
              onCleared={() => setSelectedUnitAssessmentId(null)}
            />
          ) : selectedTest?.kind === 'CUMULATIVE_ASSESSMENT' ? (
            <CumulativeAssessmentCoachingDetail
              learnerId={learnerId}
              assessmentId={selectedTest.cumulativeAssessmentId ?? null}
            />
          ) : selectedTest ? (
            <TestCoachingDetail
              learnerId={learnerId}
              timeslotId={timeslotId}
              selection={selectedTest}
            />
          ) : selectedReview && !activeReviewLesson ? (
            <DesignatedReviewCoachingDetail
              learnerId={learnerId}
              reservation={selectedReview}
              selectionMeta={effectiveSelectedLessonMeta}
              onCancelled={() => setSelectedReviewLessonId(null)}
            />
          ) : selectedHistoricalLesson || defaultHistoricalLesson ? (
            <LessonProgressDetailPane
              learnerId={learnerId}
              lessonId={(selectedHistoricalLesson ?? defaultHistoricalLesson)!.id}
              selectionMeta={(selectedHistoricalLesson ?? defaultHistoricalLesson)!.meta}
              attemptScope="progress"
            />
          ) : activeReviewLesson ? (
            <LessonGradingPane
              learnerId={learnerId}
              lesson={activeReviewLesson}
              selectionMeta={effectiveSelectedLessonMeta}
              lessonActions={renderLessonActions?.(activeReviewLesson)}
              workbookMode={workbookModeFor(activeReviewLesson.lessonId)}
              onWorkbookModeChange={(mode) => onWorkbookModeChange?.(activeReviewLesson.lessonId, mode)}
              attemptScope="review"
            />
          ) : defaultPrestartLesson || (todayLessons.length === 0 && !focusLesson) ? (
            <PrestartLessonNotice
              learnerId={learnerId}
              lessonId={defaultPrestartLesson?.id ?? selectedLessonId ?? undefined}
            />
          ) : focusLesson ? (
            <LessonGradingPane
              learnerId={learnerId}
              lesson={focusLesson}
              selectionMeta={effectiveSelectedLessonMeta}
              lessonActions={renderLessonActions?.(focusLesson)}
              workbookMode={workbookModeFor(focusLesson.lessonId)}
              onWorkbookModeChange={(mode) => onWorkbookModeChange?.(focusLesson.lessonId, mode)}
              attemptScope="progress"
            />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-12 text-center text-sm text-gray-500" role="status">
              {t('library.selectLessonPrompt')}
            </div>
          )}
        </div>
      </div>

      {historyLesson && (
        <LessonHistoryModal
          learnerId={learnerId}
          lessonId={historyLesson.id}
          lessonTitle={historyLesson.title}
          onClose={() => setHistoryLesson(null)}
        />
      )}

    </div>
  )
}

function normalizeActiveReviewLesson(lesson: LessonProcessDetailView): LessonProcessDetailView {
  if (lesson.reviewAttemptCompleted === true) return lesson
  if (lesson.status !== 'completed') return lesson
  return { ...lesson, status: 'in_progress' }
}

function findLessonSelectionMeta(library: LearnerLibraryResponse | undefined, lessonId: number | null): LessonSelectionMeta | null {
  if (!library || lessonId == null) return null

  for (const curriculum of library.tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        const lessonIndex = module.lessons.findIndex((lesson) => lesson.lessonId === lessonId)
        if (lessonIndex === -1) continue
        const lesson = module.lessons[lessonIndex]
        return {
          curriculumId: curriculum.curriculumId,
          courseId: course.courseId,
          moduleId: module.moduleId,
          chapterNumber: module.chapterNumber,
          chapterTitle: module.title,
          lessonNumber: lessonIndex + 1,
          lessonTitle: lesson.title,
          gameKeys: lesson.gameKeys ?? [],
          status: lesson.status,
        }
      }
    }
  }

  return null
}

function PrestartLessonNotice({ learnerId, lessonId }: { learnerId: number; lessonId?: number }) {
  const { t } = useTranslation('coaching')

  return (
    <div
      className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-amber-200 bg-amber-50/50 p-12 text-center text-sm text-amber-800"
      role="status"
    >
      <p>{t('library.lessonNotStartedPrompt')}</p>
      <LessonRefreshButton learnerId={learnerId} lessonId={lessonId} />
    </div>
  )
}
